import React, {
  useState,
} from "react";

import {
  Card,
  Row,
  Col,
  Typography,
  Upload,
  Button,
  Input,
  message,
} from "antd";

import {
  PlusCircleFilled,
  UploadOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const ActorSection = ({
  actors = [],
  saveActor,
  updateActor,
  deleteActor,
  savingActor,
}) => {
  const [name, setName] = useState("");
  const [svgFile, setSvgFile] = useState(null);

  const [editingActorId, setEditingActorId] =
    useState(null);

  const [editName, setEditName] =
    useState("");

  const [editSvgFile, setEditSvgFile] =
    useState(null);

  // =====================================================
  // CREATE ACTOR
  // =====================================================

  const handleSave = async () => {
    if (!name.trim()) {
      message.warning(
        "Bitte einen Namen eingeben."
      );
      return;
    }

    if (!svgFile) {
      message.warning(
        "Bitte eine SVG-Datei auswählen."
      );
      return;
    }

    const result = await saveActor(
      name,
      svgFile
    );

    if (result) {
      setName("");
      setSvgFile(null);
    }
  };

  // =====================================================
  // START EDITING
  // =====================================================

  const handleEdit = (actor) => {
    let data = actor.data;

    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch {
        data = {};
      }
    }

    setEditingActorId(actor.id);
    setEditName(actor.name || "");
    setEditSvgFile(null);
  };

  // =====================================================
  // CANCEL EDITING
  // =====================================================

  const handleCancelEdit = () => {
    setEditingActorId(null);
    setEditName("");
    setEditSvgFile(null);
  };

  // =====================================================
  // UPDATE ACTOR
  // =====================================================

  const handleUpdate = async (actor) => {
    if (!editName.trim()) {
      message.warning(
        "Bitte einen Namen eingeben."
      );
      return;
    }

    if (!updateActor) {
      message.error(
        "Actor-Update ist nicht verfügbar."
      );
      return;
    }

    const result = await updateActor(
      actor.id,
      editName,
      editSvgFile
    );

    if (result) {
      handleCancelEdit();
    }
  };

  // =====================================================
  // DELETE ACTOR
  // =====================================================

  const handleDelete = (actor) => {
    if (!deleteActor) {
      message.error(
        "Actor-Löschen ist nicht verfügbar."
      );
      return;
    }

    // The browser confirmation is enough here,
    // because the backend performs the actual safety check.
    const confirmed = window.confirm(
      `Möchten Sie den Actor "${actor.name}" wirklich löschen?`
    );

    if (!confirmed) {
      return;
    }

    deleteActor(actor.id);
  };



  // =====================================================
  // RENDER
  // =====================================================

  return (
    <Card
      className="editorContainer"
      bordered
    >
      <Title level={4}>
        Actors
      </Title>

      {/* ================================================= */}
      {/* EXISTING ACTORS */}
      {/* ================================================= */}

      <Row gutter={[16, 16]}>
        {actors.map((actor) => {
          let data = actor.data;

          if (
            typeof data === "string"
          ) {
            try {
              data = JSON.parse(data);
            } catch {
              data = {};
            }
          }

          const isEditing =
            editingActorId === actor.id;

          return (
            <Col
              key={actor.id}
              xs={24}
              sm={12}
              md={8}
              lg={6}
            >
              <Card bordered>
                {/* ========================================= */}
                {/* ACTOR PREVIEW */}
                {/* ========================================= */}

                <div
                  style={{
                    height: 150,
                    display: "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                  }}
                >
                  {data?.svg && (
                    <img
                      src={data.svg}
                      alt={actor.name}
                      style={{
                        maxWidth:
                          "100%",
                        maxHeight:
                          "140px",
                      }}
                    />
                  )}
                </div>

                {/* ========================================= */}
                {/* NORMAL MODE */}
                {/* ========================================= */}

                {!isEditing && (
                  <>
                    <Text strong>
                      {actor.name}
                    </Text>

                    <div
                      style={{
                        marginTop: 12,
                      }}
                    >
                      <Button
                        icon={
                          <EditOutlined />
                        }
                        onClick={() =>
                          handleEdit(
                            actor
                          )
                        }
                      >
                        Bearbeiten
                      </Button>

                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          handleDelete(actor)
                        }
                      >
                        Löschen
                      </Button>

                    </div>
                  </>
                )}

                {/* ========================================= */}
                {/* EDIT MODE */}
                {/* ========================================= */}

                {isEditing && (
                  <div
                    style={{
                      marginTop: 12,
                    }}
                  >
                    <Input
                      value={editName}
                      onChange={(e) =>
                        setEditName(
                          e.target.value
                        )
                      }
                      placeholder="Name des Actors"
                      style={{
                        marginBottom: 12,
                      }}
                    />

                    <Upload
                      accept=".svg,image/svg+xml"
                      maxCount={1}
                      beforeUpload={(
                        file
                      ) => {
                        if (
                          file.type !==
                          "image/svg+xml" &&
                          !file.name
                            .toLowerCase()
                            .endsWith(
                              ".svg"
                            )
                        ) {
                          message.error(
                            "Bitte eine SVG-Datei auswählen."
                          );

                          return Upload.LIST_IGNORE;
                        }

                        setEditSvgFile(
                          file
                        );

                        return false;
                      }}
                      onRemove={() => {
                        setEditSvgFile(
                          null
                        );
                      }}
                    >
                      <Button
                        icon={
                          <UploadOutlined />
                        }
                      >
                        SVG ersetzen
                      </Button>
                    </Upload>

                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        marginTop: 12,
                      }}
                    >
                      <Button
                        type="primary"
                        icon={
                          <SaveOutlined />
                        }
                        loading={
                          savingActor
                        }
                        onClick={() =>
                          handleUpdate(
                            actor
                          )
                        }
                      >
                        Speichern
                      </Button>

                      <Button
                        icon={
                          <CloseOutlined />
                        }
                        onClick={
                          handleCancelEdit
                        }
                      >
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* ================================================= */}
      {/* NEW ACTOR */}
      {/* ================================================= */}

      <Card
        bordered
        style={{
          marginTop: 24,
        }}
      >
        <Title level={5}>
          Neuen Actor hinzufügen
        </Title>

        <Input
          placeholder="Name des Actors"
          value={name}
          onChange={(e) =>
            setName(
              e.target.value
            )
          }
          style={{
            marginBottom: 12,
          }}
        />

        <Upload
          accept=".svg,image/svg+xml"
          maxCount={1}
          beforeUpload={(file) => {
            if (
              file.type !==
              "image/svg+xml" &&
              !file.name
                .toLowerCase()
                .endsWith(".svg")
            ) {
              message.error(
                "Bitte eine SVG-Datei auswählen."
              );

              return Upload.LIST_IGNORE;
            }

            setSvgFile(file);

            return false;
          }}
          onRemove={() => {
            setSvgFile(null);
          }}
        >
          <Button
            icon={
              <UploadOutlined />
            }
          >
            SVG auswählen
          </Button>
        </Upload>

        <Button
          type="primary"
          icon={
            <PlusCircleFilled />
          }
          loading={savingActor}
          onClick={handleSave}
          style={{
            marginTop: 16,
          }}
        >
          Actor speichern
        </Button>
      </Card>
    </Card>
  );
};

export default ActorSection;